import { PriceStrategies } from "@legacy/core/utils/enums"
import { sendDataToServer, valueIsDefined } from "@legacy/core/utils/utils"
import { roundHalfUp } from "@utils/roundHalfUp"
import { Component } from "react"
import deepcopy from "rfdc"

import InlinePriceOverrideDisplay from "./components/InlinePriceOverrideDisplay"
import InlinePriceOverrideForm from "./forms/InlinePriceOverrideForm"

const PAGE_MODES = {
    VIEW_ITEM: "VIEW_ITEM",
    EDIT_ITEM: "EDIT_ITEM",
}

const FORM_DATA_NAMES_BY_MODE = {
    EDIT_ITEM: "itemFormData",
}

class InlinePriceOverrideContainer extends Component {
    constructor(props) {
        super(props)

        let defaultMode = this.props.formMode || PAGE_MODES.VIEW_ITEM
        this.addToastToQueue = this.props.addToastToQueue

        const priceBookOverride = deepcopy()(this.props.priceBookOverride)
        const isNewAddition = priceBookOverride.__new__ === true

        if (isNewAddition) {
            defaultMode = PAGE_MODES.EDIT_ITEM
            delete priceBookOverride.__new__
            priceBookOverride.price_strategy = PriceStrategies.manual
            priceBookOverride.price_adjustment_amount = 0
            priceBookOverride.price_adjustment_percent = 0
        }

        this.state = {
            removed: false,
            isNewAddition: isNewAddition,
            itemData: priceBookOverride,
            itemFormData: deepcopy()(priceBookOverride),
            mode: defaultMode,
            errors: {
                overrideErrors: {},
            },
        }
    }

    calculateEstimatedPrice = (override) => {
        const basePrice = parseFloat(override.pricebook_item?.default_price) || 0

        if (override.price_strategy === PriceStrategies.fixed_adjustment) {
            const markupAmount = parseFloat(override.price_adjustment_amount) || 0
            return roundHalfUp(basePrice + markupAmount, 3)
        } else {
            const markupPercent = parseFloat(override.price_adjustment_percent) || 0
            return roundHalfUp(basePrice * (1 + markupPercent / 100), 3)
        }
    }

    dataManipulator = (data, state) => {
        const cleanData = deepcopy()(data)
        cleanData.pricebook_item = data.pricebook_item?.id
        // Strip price when price strategy is non-manual (trigger computes it)
        if (cleanData.price_strategy !== PriceStrategies.manual) {
            delete cleanData.price
        }
        return cleanData
    }

    setErrors = (fieldName, message, errorDict) => {
        if (fieldName === "non_field_errors" && message === "The fields external_client, pricebook_item must make a unique set.") {
            errorDict["pricebook_item"] = "An override for this item already exists."
        }
        else {
            errorDict[fieldName] = message
        }
    }

    addOverride = async (type, item) => {
        const dataName = "itemFormData"
        const submittingName = "submitting"
        const errorDictName = "overrideErrors"

        const endpoint =
            DjangoUrls["pricebook:api-pricebookitem-client-overrides-list"](window.MARKETPLACE_ENTITY_SLUG)
        const endpointMethod = "POST"

        const onSuccess = (createdOverride) => {
            this.setState((state, props) => {
                let updatedState = state
                updatedState.itemData = createdOverride
                updatedState.itemFormData = deepcopy()(createdOverride)
                updatedState.isNewAddition = false
                return updatedState
            })

            this.props.updateOverride(createdOverride)

            this.switchFormMode(PAGE_MODES.VIEW_ITEM)

            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: "Override created",
            })
        }

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Override could not be created",
            })
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, this.dataManipulator, this.setErrors)
    }

    deleteOverride = async (item) => {
        const errorDictName = "overrideErrors"

        const endpoint =
            DjangoUrls["pricebook:api-pricebookitem-client-overrides-detail"](
                window.MARKETPLACE_ENTITY_SLUG,
                item.id,
            )

        const onSuccess = () => {
            this.softDeleteItem()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: "Override deleted",
            })
        }

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Override could not be deleted",
            })
        }

        await sendDataToServer(this, endpoint, "DELETE", null, null, errorDictName, onSuccess, onError)
    }

    updateOverride = async (item) => {
        const dataName = "itemFormData"
        const submittingName = "submitting"
        const errorDictName = "overrideErrors"

        const endpoint =
        DjangoUrls["pricebook:api-pricebookitem-client-overrides-detail"](
            window.MARKETPLACE_ENTITY_SLUG,
            item.id,
        )
        const endpointMethod = "PUT"

        const onSuccess = (updatedOverride) => {
            this.setState((state, props) => {
                let updatedState = state
                updatedState.itemData = updatedOverride
                updatedState.itemFormData = deepcopy()(updatedOverride)
                updatedState.isNewAddition = false
                return updatedState
            })

            this.props.updateOverride(updatedOverride)

            this.switchFormMode(PAGE_MODES.VIEW_ITEM)

            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: "Override updated",
            })
        }

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Override could not be updated",
            })
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, this.dataManipulator, this.setErrors)
    }

    updateFormData = (formName, fieldName, fieldValue) => {
        this.setState((state, props) => {
            let updatedState = {
                ...state,
                [formName]: {
                    ...state[formName],
                    [fieldName]: fieldValue
                }
            }

            // Auto-update price whenever an input of its markup may have changed
            if (formName === "itemFormData" && updatedState[formName].price_strategy !== PriceStrategies.manual) {
                const estimatedPrice = this.calculateEstimatedPrice(updatedState[formName])
                updatedState[formName] = {
                    ...updatedState[formName],
                    price: estimatedPrice
                }
            }

            return updatedState
        })
    }

    switchFormMode = (mode) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState.mode = mode
            return updatedState
        })
    }

    switchToPrimaryForm = () => {
        this.switchFormMode(PAGE_MODES.EDIT_ITEM)
    }

    softDeleteItem = (json) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState.removed = true
            return updatedState
        })
    }

    validateOverride = (override) => {
        let isValid = true
        let errors = {}

        if (!valueIsDefined(override.pricebook_item) || !valueIsDefined(override.pricebook_item?.id)) {
            isValid = false
            errors.pricebook_item = "Item cannot be blank."
        }

        if (override.price_strategy === PriceStrategies.manual && !valueIsDefined(override.price)) {
            isValid = false
            errors.price = "Price cannot be blank."
        }

        return { isValid, errors }
    }

    handleOverride = (action, callback) => {
        const { isValid, errors } = this.validateOverride(this.state.itemFormData)

        if (isValid) {
            this.setState((state) => {
                let updatedState = { ...state }
                delete updatedState.itemFormData.errors
                callback(updatedState.itemData)

                return updatedState
            })
        } else {
            this.setState((state) => {
                let updatedState = { ...state }
                updatedState.itemFormData.errors = errors
                return updatedState
            })
        }
    }

    // Handle Actions

    handleActionRequest = (action) => {
        switch (action) {
            case "ITEM_EDIT":
                this.switchToPrimaryForm()
                break
            case "ITEM_ADD":
                this.handleOverride("ITEM_ADD", (itemData) => this.addOverride(itemData))
                break
            case "ITEM_SAVE":
                this.handleOverride("ITEM_SAVE", (itemData) => this.updateOverride(itemData))
                break
            case "ITEM_DELETE":
                this.deleteOverride(this.state.itemData)
                break
            case "ITEM_CANCEL_ADD":
                this.softDeleteItem()
                this.props.deleteOverride(this.state.itemData)
                break
            case "ITEM_CANCEL_EDITS":
                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.itemFormData = deepcopy()(state.itemData)
                    return updatedState
                })
                this.switchFormMode(PAGE_MODES.VIEW_ITEM)
                break

            default:
                console.error(`No action handler exists for action "${action}".`)
        }
    }

    // Render

    render = () => {
        if (!this.state.removed) {
            if (this.state.mode === PAGE_MODES.VIEW_ITEM) {
                return (
                    <InlinePriceOverrideDisplay
                        item={this.state.itemData}
                        requestAction={this.handleActionRequest}
                        formatCurrencyValue={this.props.formatCurrencyValue}
                    />
                )
            } else if (this.state.mode === PAGE_MODES.EDIT_ITEM) {
                return (
                    <InlinePriceOverrideForm
                        item={this.state.itemFormData}
                        isNewAddition={this.state.isNewAddition}
                        pricebookItemType={this.props.pricebookItemType}
                        formatCurrencyValue={this.props.formatCurrencyValue}
                        currencySymbol={this.props.currencySymbol}
                        onFormDataChange={(fieldName, fieldValue) =>
                            this.updateFormData(FORM_DATA_NAMES_BY_MODE[this.state.mode], fieldName, fieldValue)
                        }
                        requestAction={this.handleActionRequest}
                        errors={this.state.errors.overrideErrors}
                    />
                )
            } else {
                return (
                    <div className="data-panel-container data-panel-container--with-margin">
                        <div className="data-panel" aria-label="Unknown Form Mode">
                            <div className="data-panel__form">
                                <p className="data-panel__form__caption">An unhandled form mode was supplied.</p>
                            </div>
                        </div>
                    </div>
                )
            }
        } else {
            return null
        }
    }
}

export default InlinePriceOverrideContainer
